import { Modal, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { useCallback, useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { queuesApi } from 'apis/queues';
import { appActions } from 'app/app.actions';
import { HookFormQueuesSelect } from 'components/hookForm/hookFormQueuesSelect';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './conversationQueueTransferModal.module.scss';

type ConversationQueueTransferModalFormValues = {
  queueId: string[];
};

const formId = 'conversation-queue-transfer-form';

/** A component for transferring a conversation to a different queue. */
export const ConversationQueueTransferModal = createAppModal(
  { type: 'app:conversationQueueTransfer' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const containerRef = useRef<HTMLFormElement>(null);
    const csid = modal.arg.csid;

    const form = useForm<ConversationQueueTransferModalFormValues>({
      defaultValues: { queueId: [] },
    });

    /** Handles closing the transfer to queue modal. */
    const handleClose = useCallback(() => {
      ampli.modalTransferToQueueCancelClicked();
      form.reset();
      hide?.();
    }, [hide, form]);

    // Click the dropdown button to open it when modal opens
    useEffect(() => {
      if (isOpen) {
        // Small delay to ensure the modal is fully rendered
        const timer = setTimeout(() => {
          // The InputSelectionList uses a button to open the dropdown
          const button = containerRef.current?.querySelector('button');
          button?.click();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [isOpen]);

    /** Handles confirming the transfer to queue. */
    const handleConfirm: SubmitHandler<ConversationQueueTransferModalFormValues> =
      useCallback(
        async (values) => {
          const selectedQueueId = values.queueId[0];
          if (!csid || !selectedQueueId) return;

          const { data: queue } = await dispatch(
            queuesApi.endpoints.readQueue.initiate({ id: selectedQueueId }),
          ).unwrap();

          if (!queue) return;

          ampli.modalTransferToQueueConfirmClicked({
            csid,
            queueId: queue.id,
          });

          dispatch(
            appActions.conversationTransferRequest.pending({
              request: {
                type: 'internal_queue',
                csid,
                queue,
              },
            }),
          );

          handleClose();
        },
        [csid, dispatch, handleClose],
      );

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <ModalHead
          title={`Transfer #${csid} to a different queue`}
          onClose={handleClose}
        />

        <ModalBody>
          <form
            id={formId}
            ref={containerRef}
            onSubmit={form.handleSubmit(handleConfirm)}
          >
            <HookFormQueuesSelect
              name="queueId"
              control={form.control}
              rules={{
                required: true,
              }}
              noAlert
              noLabel
            />
          </form>
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleClose}
          confirmName="Transfer"
          confirmProps={{
            type: 'submit',
            form: formId,
            id: 'transfer-queue-confirm-button',
            isLoading: form.formState.isSubmitting,
            disabled: !form.formState.isValid || form.formState.isSubmitting,
          }}
          confirmOnClick={noop}
        />
      </Modal>
    );
  },
);
